/**
 * Open a new requisition — `POST /recruiting/requisitions`, WP-W1.
 *
 * A requisition is always created in `draft`; the server stamps the status and
 * `created_by`, and the form neither sends nor offers a status field. Only `title`
 * is required (the server's own `required: [title]`), so the submit is disabled while
 * it is empty — a true sentence about the operator ("you have not named the role"),
 * not about permission.
 *
 * Every other field is optional and omitted when blank rather than sent empty: an
 * absent key and a blank string are different requests, and `RecruitingRequisitionCreateRequest`
 * declares these as present-or-absent. `headcount` defaults to 1 on the server, so a
 * form left at 1 sends nothing and lets the default stand.
 *
 * The three id fields (department, hiring manager, recruiter) are **id inputs, not
 * pickers**: `GET /employees`, `GET /employees/departments` (Task T2-A) and
 * `EmployeePicker` (Task T2-B) exist now, but this form has not been rewired to use
 * them. The hint (`recruiting.jobs.create.idHint`) says so, so nobody types a name
 * and concludes the field is broken.
 */

import { useState, type ReactNode } from 'react';

import { useI18n } from '../../i18n';
import type {
  EmploymentType,
  RecruitingRequisition,
  RecruitingRequisitionCreateRequest,
} from '../../api/recruiting';
import { EMPLOYMENT_TYPES } from '../../api/recruiting';
import type { WorkforceApiError } from '../../api/errors';
import { ApiErrorNote } from '../../states/ApiErrorNote';
import { StatusNote } from '../../states/StatusNote';

const HEADCOUNT_MIN = 1;
const TITLE_MAX = 500;

export interface CreateRequisitionFormProps {
  readonly pending: boolean;
  readonly error: WorkforceApiError | null;
  readonly created: RecruitingRequisition | null;
  readonly onSubmit: (body: RecruitingRequisitionCreateRequest) => void;
}

export function CreateRequisitionForm({
  pending,
  error,
  created,
  onSubmit,
}: CreateRequisitionFormProps): ReactNode {
  const { t } = useI18n();
  const [title, setTitle] = useState('');
  const [employmentType, setEmploymentType] = useState<'' | EmploymentType>('');
  const [headcount, setHeadcount] = useState('1');
  const [location, setLocation] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const [hiringManagerId, setHiringManagerId] = useState('');
  const [recruiterId, setRecruiterId] = useState('');
  const [description, setDescription] = useState('');

  const parsedHeadcount = Number.parseInt(headcount, 10);
  const ready =
    title.trim().length > 0 && Number.isFinite(parsedHeadcount) && parsedHeadcount >= HEADCOUNT_MIN;

  const build = (): RecruitingRequisitionCreateRequest => {
    const trimmedLocation = location.trim();
    const trimmedDepartment = departmentId.trim();
    const trimmedManager = hiringManagerId.trim();
    const trimmedRecruiter = recruiterId.trim();
    const trimmedDescription = description.trim();
    return {
      title: title.trim(),
      ...(employmentType === '' ? {} : { employment_type: employmentType }),
      // 1 is the server default, so only a value above it is worth sending.
      ...(parsedHeadcount > HEADCOUNT_MIN ? { headcount: parsedHeadcount } : {}),
      ...(trimmedLocation === '' ? {} : { location: trimmedLocation }),
      ...(trimmedDepartment === '' ? {} : { department_id: trimmedDepartment }),
      ...(trimmedManager === '' ? {} : { hiring_manager_employee_id: trimmedManager }),
      ...(trimmedRecruiter === '' ? {} : { recruiter_employee_id: trimmedRecruiter }),
      ...(trimmedDescription === '' ? {} : { description: trimmedDescription }),
    };
  };

  return (
    <section className="wp-section" aria-labelledby="w1-create-heading" data-testid="w1-create">
      <h2 className="wp-section__title" id="w1-create-heading">
        {t('recruiting.jobs.create.title')}
      </h2>
      <p className="wp-small wp-muted">{t('recruiting.jobs.create.intro')}</p>

      <form
        className="wp-filters"
        onSubmit={(event) => {
          event.preventDefault();
          if (!ready || pending) return;
          onSubmit(build());
        }}
      >
        <div className="wp-filters__group">
          <label className="wp-field__label" htmlFor="w1-create-title">
            {t('recruiting.jobs.create.titleLabel')}
          </label>
          <input
            className="wp-field__input"
            id="w1-create-title"
            data-testid="w1-create-title"
            type="text"
            value={title}
            maxLength={TITLE_MAX}
            autoComplete="off"
            onChange={(event) => {
              setTitle(event.target.value);
            }}
          />
          <p className="wp-small wp-muted">{t('recruiting.jobs.create.titleRule')}</p>
        </div>

        <div className="wp-filters__group">
          <label className="wp-field__label" htmlFor="w1-create-type">
            {t('recruiting.jobs.create.employmentType')}
          </label>
          <select
            className="wp-field__input"
            id="w1-create-type"
            data-testid="w1-create-type"
            value={employmentType}
            onChange={(event) => {
              setEmploymentType(event.target.value as '' | EmploymentType);
            }}
          >
            <option value="">{t('recruiting.jobs.employmentType.none')}</option>
            {EMPLOYMENT_TYPES.map((type) => (
              <option key={type} value={type}>
                {t(`recruiting.jobs.employmentType.${type}`)}
              </option>
            ))}
          </select>
        </div>

        <div className="wp-filters__group">
          <label className="wp-field__label" htmlFor="w1-create-headcount">
            {t('recruiting.jobs.create.headcount')}
          </label>
          <input
            className="wp-field__input"
            id="w1-create-headcount"
            data-testid="w1-create-headcount"
            type="number"
            min={HEADCOUNT_MIN}
            value={headcount}
            onChange={(event) => {
              setHeadcount(event.target.value);
            }}
          />
        </div>

        <div className="wp-filters__group">
          <label className="wp-field__label" htmlFor="w1-create-location">
            {t('recruiting.jobs.create.location')}
          </label>
          <input
            className="wp-field__input"
            id="w1-create-location"
            data-testid="w1-create-location"
            type="text"
            value={location}
            autoComplete="off"
            onChange={(event) => {
              setLocation(event.target.value);
            }}
          />
        </div>

        <div className="wp-filters__group">
          <label className="wp-field__label" htmlFor="w1-create-department">
            {t('recruiting.jobs.create.department')}
          </label>
          <input
            className="wp-field__input"
            id="w1-create-department"
            data-testid="w1-create-department"
            type="text"
            value={departmentId}
            autoComplete="off"
            onChange={(event) => {
              setDepartmentId(event.target.value);
            }}
          />
        </div>

        <div className="wp-filters__group">
          <label className="wp-field__label" htmlFor="w1-create-manager">
            {t('recruiting.jobs.create.hiringManager')}
          </label>
          <input
            className="wp-field__input"
            id="w1-create-manager"
            data-testid="w1-create-manager"
            type="text"
            value={hiringManagerId}
            autoComplete="off"
            onChange={(event) => {
              setHiringManagerId(event.target.value);
            }}
          />
        </div>

        <div className="wp-filters__group">
          <label className="wp-field__label" htmlFor="w1-create-recruiter">
            {t('recruiting.jobs.create.recruiter')}
          </label>
          <input
            className="wp-field__input"
            id="w1-create-recruiter"
            data-testid="w1-create-recruiter"
            type="text"
            value={recruiterId}
            autoComplete="off"
            onChange={(event) => {
              setRecruiterId(event.target.value);
            }}
          />
          <p className="wp-small wp-muted">{t('recruiting.jobs.create.idHint')}</p>
        </div>

        <div className="wp-filters__group">
          <label className="wp-field__label" htmlFor="w1-create-description">
            {t('recruiting.jobs.create.description')}
          </label>
          <textarea
            className="wp-field__input"
            id="w1-create-description"
            data-testid="w1-create-description"
            value={description}
            rows={3}
            onChange={(event) => {
              setDescription(event.target.value);
            }}
          />
        </div>

        <div className="wp-filters__actions">
          <button
            className="wp-button wp-button--primary"
            type="submit"
            data-testid="w1-create-submit"
            disabled={!ready || pending}
          >
            {pending ? t('recruiting.jobs.create.submitting') : t('recruiting.jobs.create.submit')}
          </button>
        </div>
      </form>

      {error === null ? null : (
        <ApiErrorNote
          error={error}
          subject={t('recruiting.jobs.subjectWord')}
          testId="w1-create-error"
        />
      )}
      {created === null ? null : (
        <StatusNote
          severity="low"
          title={t('recruiting.jobs.create.ok', { title: created.title })}
          testId="w1-create-ok"
        />
      )}
    </section>
  );
}
